import { AcousticSettingsPreset } from '../../models/AcousticSettings';
import { IAudioFile } from '../../models/AudioFiles';
import { ILoadedAudioFile } from '../../models/LoadedAudioFiles';
import { IProjectSaveFile } from '../../models/ProjectFile';
import { ITrackId } from '../../models/Selection';
import { SetPieceColor, SetPieceShape } from '../../models/SetPieces';
import { Units } from '../../models/Settings';
import { StageShape } from '../../models/Stage';
import {
	DancerPathColor,
	DancerPathNodeShape,
	IAudioClip,
	IAudioGapId,
} from '../../models/Timeline';
import { IReverbSettings } from '../../utils/reverbEffect';
import { IVec2 } from '../../utils/vec2';
import { ActionType } from '../action-types';

// create action types so you can define the payload and type for easy creation in reducers
export interface UndoAction {
	type: ActionType.UNDO;
}

export interface RedoAction {
	type: ActionType.REDO;
}

export interface ImportFilesAction {
	type: ActionType.IMPORT_FILES;
	payload: {
		files: File[];
	};
}

export interface AddAudioFileAction {
	type: ActionType.ADD_AUDIO_FILE;
	payload: {
		audioFile: IAudioFile;
	};
}

export interface AudioFileLoadedAction {
	type: ActionType.AUDIO_FILE_LOADED;
	payload: {
		loadedAudioFile: ILoadedAudioFile;
	};
}

export interface DeleteAudioFileAction {
	type: ActionType.DELETE_AUDIO_FILE;
	payload: {
		id: number;
	};
}

export interface PlayAudioFileAction {
	type: ActionType.PLAY_AUDIO_FILE;
	payload: {
		id: number;
	};
}

export interface PlayTimelineAction {
	type: ActionType.PLAY_TIMELINE;
	payload: {
		time: number;
	};
}

export enum ExportFormat {
	Wav,
	Flac,
}

export interface ExportTimelineAction {
	type: ActionType.EXPORT_TIMELINE;
	payload: {
		fileName: string;
		format: ExportFormat;
		sampleRate: number;
	};
}

export interface SetTimelineTimeAction {
	type: ActionType.SET_TIMELINE_TIME;
	payload: {
		time: number;
	};
}

export interface UpdateTimelineTimeAction {
	type: ActionType.UPDATE_TIMELINE_TIME;
	payload: {
		time: number;
	};
}

export interface StopPlaybackAction {
	type: ActionType.STOP_PLAYBACK;
}

export interface InsertAudioClipAction {
	type: ActionType.INSERT_AUDIO_CLIP;
	payload: {
		audioFileId: number;
		time: number;
		duration: number;
		trackId: ITrackId | null;
	};
}

export interface DeleteAudioClipAction {
	type: ActionType.DELETE_AUDIO_CLIP;
	payload: {
		clipId: number;
	};
}

export interface SelectAudioClipAction {
	type: ActionType.SELECT_AUDIO_CLIP;
	payload: {
		clip: IAudioClip | null;
	};
}

export interface SetStageShape {
	type: ActionType.SET_STAGE_SHAPE;
	payload: {
		shape: StageShape;
	};
}

export interface SetStageSize {
	type: ActionType.SET_STAGE_SIZE;
	payload: {
		size: IVec2;
	};
}

export interface SetUnits {
	type: ActionType.SET_UNITS;
	payload: {
		units: Units;
	};
}

export interface AddSetPieceAction {
	type: ActionType.ADD_SET_PIECE;
}

export interface SetSetPieceRectAction {
	type: ActionType.SET_SET_PIECE_RECT;
	payload: {
		id: number;
		pos: IVec2;
		size: IVec2;
		rotation: number;
	};
}

export interface SetAudiencePosRotationAction {
	type: ActionType.SET_AUDIENCE_POS_ROTATION;
	payload: {
		pos: IVec2;
		rotation: number;
	};
}

export interface SetSelectedSetPieceAction {
	type: ActionType.SET_SELECTED_SET_PIECE;
	payload: {
		id: number | 'audience';
	};
}

export interface SetSelectedDancerAction {
	type: ActionType.SET_SELECTED_DANCER;
	payload: {
		id: number | null;
	};
}

export interface UpdateFilter {
	type: ActionType.UPDATE_FILTER;
	payload: {
		filter: string;
	};
}

export interface SetTrackMutedAction {
	type: ActionType.SET_TRACK_MUTED;
	payload: {
		trackId: ITrackId;
		muted: boolean;
	};
}

export interface SetAllTracksMutedAction {
	type: ActionType.SET_ALL_TRACKS_MUTED;
	payload: {
		muted: boolean;
	};
}

export interface SaveProjectAction {
	type: ActionType.SAVE_PROJECT;
}

export interface ActivateProjectAction {
	type: ActionType.ACTIVATE_PROJECT;
}

export interface LoadProjectAction {
	type: ActionType.LOAD_PROJECT;
	payload: {
		savedFile: IProjectSaveFile;
	};
}

export interface MoveAudioClipAction {
	type: ActionType.MOVE_AUDIO_CLIP;
	payload: {
		audioClipId: number;
		trackId: ITrackId;
		time: number;
	};
}

export interface SetCurrentStateAction {
	type: ActionType.SET_CURRENT_STATE;
	payload: {
		undoable: any;
	};
}

export interface ResetStateAction {
	type: ActionType.RESET_STATE;
}

export interface UpdateSetPieceName {
	type: ActionType.UPDATE_SET_PIECE_NAME;
	payload: {
		id: number;
		name: string;
	};
}

export interface UpdateSetPieceAppearance {
	type: ActionType.UPDATE_SET_PIECE_APPEARANCE;
	payload: {
		id: number;
		shape: SetPieceShape;
		color: SetPieceColor;
	};
}

export interface DeleteSetPiece {
	type: ActionType.DELETE_SET_PIECE;
	payload: {
		id: number;
	};
}

export interface UpdateSetPieceName {
	type: ActionType.UPDATE_SET_PIECE_NAME;
	payload: {
		id: number;
		name: string;
	};
}

export interface UpdateSetPieceAppearance {
	type: ActionType.UPDATE_SET_PIECE_APPEARANCE;
	payload: {
		id: number;
		shape: SetPieceShape;
		color: SetPieceColor;
	};
}

export interface DeleteSetPiece {
	type: ActionType.DELETE_SET_PIECE;
	payload: {
		id: number;
	};
}

export interface AddDancerPathNode {
	type: ActionType.ADD_DANCER_PATH_NODE;
	payload: {
		dancerId: number;
		time: number;
		delay: number;
		pos: IVec2;
	};
}

export interface SetDancerPathNode {
	type: ActionType.SET_DANCER_PATH_NODE;
	payload: {
		dancerId: number;
		pathNodeId: number;
		name: string;
		time: number | undefined;
		delay: number | undefined;
		pos: IVec2;
	};
}

export interface SetDancerPathNodeDuration {
	type: ActionType.SET_DANCER_PATH_NODE_DURATION;
	payload: {
		dancerId: number;
		pathNodeId: number;
		duration: number;
	};
}

export interface DeleteDancerPathNode {
	type: ActionType.DELETE_DANCER_PATH_NODE;
	payload: {
		dancerId: number;
		pathNodeId: number;
	};
}

export interface MoveFutureDancerPathNodes {
	type: ActionType.MOVE_FUTURE_DANCER_PATH_NODE;
	payload: {
		dancerId: number;
		firstPathNodeId: number;
		timeOffset: number;
	};
}

export interface AddDancerAction {
	type: ActionType.ADD_DANCER;
}

export interface DeleteDancerAction {
	type: ActionType.DELETE_DANCER;
	payload: {
		dancerId: number;
	};
}

export interface SetDancerNameAction {
	type: ActionType.SET_DANCER_NAME;
	payload: {
		id: number;
		name: string;
	};
}

export interface SetDancerPathAppearance {
	type: ActionType.SET_DANCER_PATH_APPEARANCE;
	payload: {
		id: number;
		color: DancerPathColor;
		shape: DancerPathNodeShape;
	};
}

export interface SetDancerHeight {
	type: ActionType.SET_DANCER_HEIGHT;
	payload: {
		id: number;
		height: number;
	};
}

export interface SetAcousticSettingsPreset {
	type: ActionType.SET_ACOUSTIC_SETTINGS_PRESET;
	payload: {
		preset: AcousticSettingsPreset;
	};
}

export interface SetCustomAcousticSettings {
	type: ActionType.SET_CUSTOM_ACOUSTIC_SETTINGS;
	payload: {
		reverbSettings: IReverbSettings;
	};
}

export interface SelectDancerPathNodeAction {
	type: ActionType.SELECT_DANCER_PATH_NODE;
	payload: {
		dancerId: number;
		pathNodeId: number;
	} | null;
}

export interface DeleteAudioClipsAtTimeAction {
	type: ActionType.DELETE_AUDIO_CLIPS_AT_TIME;
	payload: {
		time: number;
	};
}

export interface SelectAudioGapAction {
	type: ActionType.SELECT_AUDIO_GAP;
	payload: IAudioGapId | null;
}

export interface DeleteAudioGapAction {
	type: ActionType.DELETE_AUDIO_GAP;
	payload: IAudioGapId;
}

export interface SetShortcutsEnabled {
	type: ActionType.SET_SHORTCUTS_ENABLED;
	payload: {
		newValue: boolean;
	};
}

// to add more to action, add a '|' and the new action type
// this list can get huge depending on how any action types you create, but it is okay
// if you want, you can split up action types and then just combine them in the index file
export type Action =
	| UndoAction
	| RedoAction
	| ImportFilesAction
	| AddAudioFileAction
	| AudioFileLoadedAction
	| DeleteAudioFileAction
	| PlayAudioFileAction
	| PlayTimelineAction
	| ExportTimelineAction
	| SetTimelineTimeAction
	| UpdateTimelineTimeAction
	| StopPlaybackAction
	| InsertAudioClipAction
	| DeleteAudioClipAction
	| SelectAudioClipAction
	| SetStageShape
	| SetStageSize
	| SetUnits
	| AddSetPieceAction
	| SetSetPieceRectAction
	| SetAudiencePosRotationAction
	| SetSelectedSetPieceAction
	| SetSelectedDancerAction
	| UpdateFilter
	| SetTrackMutedAction
	| SetAllTracksMutedAction
	| MoveAudioClipAction
	| SaveProjectAction
	| ActivateProjectAction
	| LoadProjectAction
	| SetCurrentStateAction
	| ResetStateAction
	| UpdateSetPieceName
	| UpdateSetPieceAppearance
	| DeleteSetPiece
	| AddDancerPathNode
	| SetDancerPathNode
	| SetDancerPathNodeDuration
	| DeleteDancerPathNode
	| MoveFutureDancerPathNodes
	| AddDancerAction
	| DeleteDancerAction
	| SetDancerNameAction
	| SetDancerPathAppearance
	| SetDancerHeight
	| SetAcousticSettingsPreset
	| SetCustomAcousticSettings
	| SelectDancerPathNodeAction
	| DeleteAudioClipsAtTimeAction
	| SelectAudioGapAction
	| DeleteAudioGapAction
	| SetShortcutsEnabled;
